import { ArrowUpwardRounded } from '@mui/icons-material';
import { Box, IconButton, SxProps, Theme, alpha } from '@mui/material';
import { throttle } from 'lodash';
import { RefObject, useEffect, useState } from 'react';

interface ScrollToTopButtonProps {
  parentRef?: RefObject<HTMLElement>;
  containerSx?: SxProps<Theme>;
  visibilityOffset?: number;
  onClick?: () => void;
}

export const ScrollToTopButton = ({
  parentRef,
  containerSx,
  visibilityOffset = 300,
  onClick,
}: ScrollToTopButtonProps) => {
  const [isVisible, setIsVisible] = useState(false);

  useEffect(() => {
    const handleScroll = throttle(() => {
      const scrollTop = parentRef?.current
        ? parentRef.current.scrollTop
        : window.scrollY || document.documentElement.scrollTop;

      setIsVisible(scrollTop > visibilityOffset);
    }, 250);

    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, [parentRef, visibilityOffset]);

  const scrollToTop = () => {
    const scrollToElement = parentRef?.current
      ? parentRef.current
      : document.documentElement;

    scrollToElement.scrollTo({
      top: 0,
      behavior: 'smooth',
    });
  };

  const handleClick = () => {
    scrollToTop();
    onClick?.();
  };

  return (
    <Box
      sx={{
        position: 'fixed',
        bottom: '10px',
        left: '50%',
        transform: 'translate(-50%, -50%)',
        zIndex: (theme) => theme.zIndex.fab,
        ...containerSx,
      }}
    >
      <IconButton
        onClick={handleClick}
        sx={{
          backgroundColor: (theme) =>
            `${alpha(theme.palette.text.primary, 0.08)} !important`,
          transform: isVisible ? 'scale(1)' : 'scale(0)',
          transition: 'transform 0.2s ease-in-out',
          '&:hover': {
            backgroundColor: (theme) =>
              `${theme.palette.action.hover} !important`,
          },
        }}
      >
        <ArrowUpwardRounded />
      </IconButton>
    </Box>
  );
};
